import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Modal from "react-modal";
import IGetCatalogsDTO from "../../../models/DTO/IGetCatalogsDTO";
import IGetVendorSourcesCatalogItemsCsvDTO from "../../../models/DTO/IGetVendorSourcesCatalogItemsCsvDTO";
import IGetVendorSourcesCsvDTO from "../../../models/DTO/IGetVendorSourcesCsvDTO";
import IGetVendorSourcesDTO from "../../../models/DTO/IGetVendorSourcesDTO";
import IGetVendorSourcesLocationsCsvDTO from "../../../models/DTO/IGetVendorSourcesLocationsCsvDTO";
import IOnGetCatalogsDTO from "../../../models/DTO/IOnGetCatalogsDTO";
import IOnGetVendorSourcesCatalogItemsCsvDTO from "../../../models/DTO/IOnGetVendorSourcesCatalogItemsCsvDTO";
import IOnGetVendorSourcesCsvDTO from "../../../models/DTO/IOnGetVendorSourcesCsvDTO";
import IOnGetVendorSourcesDTO from "../../../models/DTO/IOnGetVendorSourcesDTO";
import IOnGetVendorSourcesLocationsCsvDTO from "../../../models/DTO/IOnGetVendorSourcesLocationsCsvDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IAction from "../../../models/IAction";
import ICatalog from "../../../models/ICatalog";
import IOwTableCols from "../../../models/IOwTableCols";
import IVendor_Source from "../../../models/IVendor_Source";
import UIService from "../../../services/UIService";
import VendorSourceService from "../../../services/VendorSourceService";
import AddressDisplay from "../../shared/AddressDisplay/AddressDisplay";
import OwActionBar from "../../shared/OwActionBar/OwActionBar";
import OwButton from "../../shared/OwButton/OwButton";
import OwGoogleStaticMap from "../../shared/OwGoogleStaticMap/OwGoogleStaticMap";
import OwInputWrapper from "../../shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwTable from "../../shared/OwTable/OwTable";
import OwTitle from "../../shared/OwTitle/OwTitle";
import PaginationBar from "../../shared/PaginationBar/PaginationBar";
import { VendorSourceCatalogItemsDetailView } from "../VendorSourceCatalogItemsDetailView/VendorSourceCatalogItemsDetailView";
import VendorSourceLocationDetailView from "../VendorSourceLocationDetailView/VendorSourceLocationDetailView";
import { VendorSourceFnSidebar } from "../VendorSourceFnSidebar/VendorSourceFnSidebar";
import VendorSourcesUploadModal from "../VendorSourcesUploadModal/VendorSourcesUploadModal";
import IQBOPullVendorsDTO from "../../../models/DTO/IQBOPullVendorsDTO";
import INotification from "../../../models/INotification";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import { VendorSourcesHeader } from "../VendorSourcesHeader/VendorSourcesHeader";
import IQBOPullAndDownloadVendorsDTO from "../../../models/DTO/IQBOPullAndDownloadVendorsDTO";
import IQBOOnPullAndDownloadVendorsDTO from "../../../models/DTO/IQBOOnPullAndDownloadVendorsDTO";
import IntegrationsConnectedBar from "../../shared/IntegrationsConnectedBar/IntegrationsConnectedBar";
import IProcessVendorSourceDTO from "../../../models/DTO/IProcessVendorSourceDTO";
import IQBDCreateVendorSourcesDTO from "../../../models/DTO/IQBDCreateVendorSourcesDTO";
import { ConfirmDeleteOverlayFn } from "../../shared/ConfirmDeleteOverlayFn/ConfirmDeleteOverlayFn";
import { OwDeleteEditActions } from "../../shared/OwDeleteEditActions/OwDeleteEditActions";
import { SearchSection } from "../../shared/SearchSection/SearchSection";
import { useAppContextNonNull } from "../../../contexts/AppContext";
import "./VendorSourcesFn.scss";
import { QboPushVendorsModal } from "../QboPushVendorsModal/QboPushVendorsModal";
import { ProcessResultModal } from "../../shared/ProcessResultModal/ProcessResultModal";

type Props = {};

export const VendorSourcesFn = (props: Props) => {
    // Context
    const { socket, userModel, supplierModel, uiContext } = useAppContextNonNull();
    const location = useLocation();
    const navigate = useNavigate();

    // Parse URL parameters
    const params = new URLSearchParams(location.search);
    const urlName = params.get("name");

    // State - 19 useState hooks
    const [vendorSources, setVendorSources] = useState<IVendor_Source[]>([]);
    const [totalVendorSources, setTotalVendorSources] = useState<number>(0);
    const [offset, setOffset] = useState<number>(0);
    const [limit, setLimit] = useState<number>(25);
    const [isDataFetching, setIsDataFetching] = useState<boolean>(false);
    const [isInitialData, setIsInitialData] = useState<boolean>(true);
    const [qboActive, setQboActive] = useState<boolean>(false);
    const [qbdActive, setQbdActive] = useState<boolean>(false);
    const [name, setName] = useState<string>(urlName || "");
    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
    const [modalComponent, setModalComponent] = useState<React.ReactNode>(null);
    const [catalogs, setCatalogs] = useState<ICatalog[]>([]);
    const [selectedVendorSource, setSelectedVendorSource] = useState<IVendor_Source | null>(null);
    const [deleteRow, setDeleteRow] = useState<string | null>(null);

    // Socket emit functions - wrapped with useCallback
    const getVendorSources = () => {
        const payload: IGetVendorSourcesDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            limit: limit,
            offset: offset,
            includeCatalogItems: false,
        };

        if (name.length) {
            payload.queryString = `?name=${name}`;
        }

        socket.emit(EndpointsEnum.PurchaseOrderGetVendorSources, payload);
        setIsDataFetching(true);
    };

    const getCatalogs = useCallback(() => {
        const payload: IGetCatalogsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            callMeta: {
                view: "VendorSources",
                fn: "getCatalogs",
            },
        };

        socket.emit(EndpointsEnum.CatalogsGetCatalogs, payload);
    }, [socket, userModel.guid, userModel.token, supplierModel.guid]);

    const downloadVendorSourcesCSV = useCallback(() => {
        const payload: IGetVendorSourcesCsvDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.PurchaseOrderGetVendorSourcesCSV, payload);
    }, [socket, userModel.guid, userModel.token, supplierModel.guid]);

    const downloadVendorSourcesLocationsCSV = useCallback(() => {
        const payload: IGetVendorSourcesLocationsCsvDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.PurchaseOrderGetVendorSourcesLocationsCSV, payload);
    }, [socket, userModel.guid, userModel.token, supplierModel.guid]);

    const downloadVendorSourcesCatalogItemsCSV = useCallback(() => {
        const payload: IGetVendorSourcesCatalogItemsCsvDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.PurchaseOrderGetVendorSourcesCatalogItemsCSV, payload);
    }, [socket, userModel.guid, userModel.token, supplierModel.guid]);

    const pushQboVendors = async (options: { onlyUnconnected: boolean }) => {
        setQboActive(true);
        setModalIsOpen(false);
        setModalComponent(null);

        try {
            const result = await VendorSourceService.pushQboVendors({ onlyUnconnected: options.onlyUnconnected }, userModel, supplierModel);

            const notification: INotification = {
                message: "successfully_pushed_vendors",
                position: NotificationPositionEnum.bc,
                level: NotificationLevelEnum.success,
            };

            uiContext.notifyFn(null, notification);

            const modalComponent = <ProcessResultModal closeModalFn={closeModal} result={result} title="push_vendors_results" />;
            setModalIsOpen(true);
            setModalComponent(modalComponent);

            getVendorSources();
        } catch (error) {
            const notification: INotification = {
                message: "failed_to_push_vendors",
                position: NotificationPositionEnum.bc,
                level: NotificationLevelEnum.error,
            };

            uiContext.notifyFn(null, notification);
            console.error("Push Vendors Error:", error);
        } finally {
            setQboActive(false);
        }
    };

    const showPushQboVendorsModal = () => {
        const modalComponent = <QboPushVendorsModal closeModalFn={closeModal} startPushFn={pushQboVendors} />;

        setModalIsOpen(true);
        setModalComponent(modalComponent);
    };

    const connectQboVendors = async () => {
        setQboActive(true);
        setModalIsOpen(false);
        setModalComponent(null);

        try {
            const result = await VendorSourceService.connectQboVendors({}, userModel, supplierModel);

            const notification: INotification = {
                message: "successfully_connected_vendors",
                position: NotificationPositionEnum.bc,
                level: NotificationLevelEnum.success,
            };

            uiContext.notifyFn(null, notification);

            const modalComponent = <ProcessResultModal closeModalFn={closeModal} result={result} title="connect_vendors_results" />;
            setModalIsOpen(true);
            setModalComponent(modalComponent);

            getVendorSources();
        } catch (error) {
            const notification: INotification = {
                message: "failed_to_connect_vendors",
                position: NotificationPositionEnum.bc,
                level: NotificationLevelEnum.error,
            };

            uiContext.notifyFn(null, notification);
            console.error("Connect Vendors Error:", error);
        } finally {
            setQboActive(false);
        }
    };

    const pullQboVendors = useCallback(() => {
        const payload: IQBOPullVendorsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.QBOPullVendors, payload);
        setQboActive(true);
    }, [socket, userModel.guid, userModel.token, supplierModel.guid]);

    const downloadQboVendors = useCallback(() => {
        const payload: IQBOPullAndDownloadVendorsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.QBOPullAndDownloadVendors, payload);
        setQboActive(true);
    }, [socket, userModel.guid, userModel.token, supplierModel.guid]);

    const syncQbdVendors = useCallback(() => {
        const payload: IQBDCreateVendorSourcesDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.QBDCreateVendorSourcesFromVendors, payload);
        setQbdActive(true);
    }, [socket, userModel.guid, userModel.token, supplierModel.guid]);

    // Socket event handlers - wrapped with useCallback
    const onGetVendorSources = useCallback((data: IOnGetVendorSourcesDTO) => {
        setVendorSources(data.vendorSources);
        setTotalVendorSources(data.totalVendorSources);
        setIsDataFetching(false);
        setIsInitialData(false);
    }, []);

    const onGetVendorSourcesCSV = useCallback((data: IOnGetVendorSourcesCsvDTO) => {
        if (data) {
            UIService.downloadCSVData(data.csvData, data.filename);
        }
    }, []);

    const onGetVendorSourcesLocationsCSV = useCallback((data: IOnGetVendorSourcesLocationsCsvDTO) => {
        if (data) {
            UIService.downloadCSVData(data.csvData, data.filename);
        }
    }, []);

    const onGetVendorSourcesCatalogItemsCSV = useCallback((data: IOnGetVendorSourcesCatalogItemsCsvDTO) => {
        if (data) {
            UIService.downloadCSVData(data.csvData, data.filename);
        }
    }, []);

    const onGetCatalogs = useCallback((data: IOnGetCatalogsDTO) => {
        setCatalogs(data.catalogs);
    }, []);

    const onProcessVendorSourceCatalogItem = useCallback(() => {
        getVendorSources();
    }, [getVendorSources]);

    const onPullQboVendors = useCallback(() => {
        const notification: INotification = {
            message: "successfully_pulled_vendors",
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
        };

        uiContext.notifyFn(null, notification);
        setQboActive(false);
        getVendorSources();
    }, [uiContext, getVendorSources]);

    const onDownloadQboVendors = useCallback((data: IQBOOnPullAndDownloadVendorsDTO) => {
        if (data) {
            UIService.downloadCSVData(data.csvData, data.filename);
            setQboActive(false);
        }
    }, []);

    const onSyncQbdVendors = useCallback(() => {
        const notification: INotification = {
            message: "successfully_synced_vendors",
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
        };

        uiContext.notifyFn(null, notification);
        setQbdActive(false);
        getVendorSources();
    }, [uiContext, getVendorSources]);

    const onProcessVendorSource = useCallback(() => {
        const notification: INotification = {
            message: "successfully_processed_vendor_source",
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
        };

        uiContext.notifyFn(null, notification);
        setSelectedVendorSource(null);
        setDeleteRow(null);
        uiContext.setActivityIndicator(false);
        getVendorSources();
    }, [uiContext, getVendorSources]);

    const notify = useCallback(() => {
        setQboActive(false);
    }, []);

    // UI Interaction Methods - wrapped with useCallback
    const cancelSidebar = useCallback(
        (refreshData?: boolean) => {
            uiContext.setSidebarComponent(null);
            uiContext.toggleSidebar(false);

            if (refreshData) {
                setName("");
                getVendorSources();
            }
        },
        [uiContext, getVendorSources]
    );

    const closeModal = useCallback(() => {
        setModalIsOpen(false);
        setModalComponent(null);
        getVendorSources();
    }, [getVendorSources]);

    const openVendorSourceSidebar = useCallback(
        (vendorSource?: IVendor_Source) => {
            const component = <VendorSourceFnSidebar cancelFn={cancelSidebar} vendorSource={vendorSource} />;

            uiContext.setSidebarComponent(component);
            uiContext.toggleSidebar(true);
        },
        [cancelSidebar, uiContext]
    );

    const showUploadModal = useCallback(() => {
        const component = (
            <VendorSourcesUploadModal
                closeModalFn={closeModal}
                socket={socket}
                notifyFn={uiContext.notifyFn}
                userModel={userModel}
                supplierModel={supplierModel}
            />
        );

        setModalIsOpen(true);
        setModalComponent(component);
    }, [closeModal, socket, uiContext.notifyFn, userModel, supplierModel]);

    const confirmDelete = useCallback((row: IVendor_Source, index: number) => {
        setSelectedVendorSource(row);
        setDeleteRow(`row-${index}`);
    }, []);

    const cancelDelete = useCallback(() => {
        setDeleteRow(null);
    }, []);

    const commitDelete = useCallback(() => {
        if (selectedVendorSource) {
            const payload: IProcessVendorSourceDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorSource: selectedVendorSource,
                deleteFlag: true,
            };

            socket.emit(EndpointsEnum.PurchaseOrderProcessVendorSource, payload);
            uiContext.setActivityIndicator(true);
        }
    }, [selectedVendorSource, socket, userModel.guid, userModel.token, supplierModel.guid, uiContext]);

    const paginationChanged = useCallback((newOffset: number, newLimit: number) => {
        setOffset(newOffset);
        setLimit(newLimit);
    }, []);

    const handleChange = useCallback((e: ChangeEvent<any>) => {
        const value = e.target.value;
        const fieldName = e.target.name;

        if (fieldName === "name") {
            setName(value);
        }
    }, []);

    const search = useCallback(() => {
        getVendorSources();
    }, [getVendorSources]);

    const reset = useCallback(() => {
        setName("");
        navigate("/vendor_sources");
        getVendorSources();
    }, [navigate, getVendorSources]);

    const enterSearch = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
                search();
            }
        },
        [search]
    );

    // Render functions - wrapped with useCallback
    const renderActionsFn = useCallback(
        (row: IVendor_Source, index: number) => {
            return (
                <div className={"d-flex"}>
                    <OwDeleteEditActions
                        isEditMode={false}
                        onTrashClickFn={() => {
                            confirmDelete(row, index);
                        }}
                        onEditClickFn={() => {
                            openVendorSourceSidebar(row);
                        }}
                    />
                </div>
            );
        },
        [confirmDelete, openVendorSourceSidebar]
    );

    const renderNameFn = useCallback(
        (row: IVendor_Source) => {
            return (
                <div>
                    <div className={"d-flex"}>
                        <div>{row.name}</div>

                        <IntegrationsConnectedBar
                            position={"left"}
                            supplierModel={supplierModel}
                            qboConnected={!!row.qbo_VendorId}
                            qbdConnected={!!row.qbd_ListID}
                            showIntegrations={{
                                quickbooksOnline: supplierModel.Supplier_Integration.has_qbo,
                                quickbooksDesktop: supplierModel.Supplier_Integration.has_qbd,
                            }}
                        />
                    </div>
                    {!row.customer_no ? null : (
                        <div className={"text-muted"}>
                            <OwLabel className={"me-1"} title={"customer_no"} />
                            {row.customer_no}
                        </div>
                    )}
                    <Link to={`/vendor_sources/${row.guid}/detail`}>
                        <div>
                            <OwLabel title="detail_view" />
                        </div>
                    </Link>
                </div>
            );
        },
        [supplierModel]
    );

    const renderBillToFn = useCallback(
        (row: IVendor_Source) => {
            if (row.Bill_To) {
                return (
                    <div className={"d-flex"}>
                        {supplierModel.has_google_maps ? (
                            <OwGoogleStaticMap address={row.Bill_To} className={"me-2"} size={"120x120"} />
                        ) : null}
                        <AddressDisplay address={row.Bill_To} showName={true} showCompanyName={true} />
                    </div>
                );
            } else {
                return null;
            }
        },
        [supplierModel.has_google_maps]
    );

    const renderShipToFn = useCallback(
        (row: IVendor_Source) => {
            if (row.Ship_To) {
                return (
                    <div className={"d-flex"}>
                        {supplierModel.has_google_maps ? (
                            <OwGoogleStaticMap address={row.Ship_To} className={"me-2"} size={"120x120"} />
                        ) : null}
                        <AddressDisplay address={row.Ship_To} showName={true} showCompanyName={true} />
                    </div>
                );
            } else {
                return null;
            }
        },
        [supplierModel.has_google_maps]
    );

    const getDetailView = useCallback(
        (row: IVendor_Source, index: number) => {
            return (
                <div>
                    <VendorSourceLocationDetailView
                        vendorSource={row}
                        userModel={userModel}
                        supplierModel={supplierModel}
                        setActivityIndicator={uiContext.setActivityIndicator}
                        socket={socket}
                        notifyFn={uiContext.notifyFn}
                        toggleSidebar={uiContext.toggleSidebar}
                        refreshVendorSources={getVendorSources}
                        setSidebarComponent={uiContext.setSidebarComponent}
                    />

                    <div className={"mt-3"}>
                        <VendorSourceCatalogItemsDetailView catalogs={catalogs} vendorSource={row} parentRowIndex={index} />
                    </div>
                </div>
            );
        },
        [userModel, supplierModel, socket, uiContext, catalogs, getVendorSources]
    );

    // Actions array - memoized with dependencies
    const actions: IAction[] = useMemo(() => {
        const actionsList: IAction[] = [
            {
                isDropdownGroup: true,
                name: "download_csv",
                groupActions: [
                    {
                        leftIconClass: "fad fa-download",
                        name: "vendor_sources",
                        fn: downloadVendorSourcesCSV,
                        disabledFn: () => {
                            return false;
                        },
                    },
                    {
                        leftIconClass: "fad fa-download",
                        name: "vendor_sources_locations",
                        fn: downloadVendorSourcesLocationsCSV,
                        disabledFn: () => {
                            return false;
                        },
                    },
                    {
                        leftIconClass: "fad fa-download",
                        name: "vendor_sources_catalog_items",
                        fn: downloadVendorSourcesCatalogItemsCSV,
                        disabledFn: () => {
                            return false;
                        },
                    },
                ],
            },
            {
                dropdownVariant: "danger",
                isDropdownGroup: true,
                name: "upload_csv",
                groupActions: [
                    {
                        leftIconClass: "fad fa-upload",
                        name: "upload",
                        fn: showUploadModal,
                        disabledFn: () => {
                            return false;
                        },
                        btnClass: "btn-danger",
                    },
                ],
            },
        ];

        if (supplierModel.Supplier_Integration.has_qbd) {
            actionsList.push({
                isDropdownGroup: true,
                name: "quickbooks_desktop",
                groupActions: [
                    {
                        name: "sync_vendors",
                        fn: syncQbdVendors,
                        disabledFn: () => {
                            return qbdActive;
                        },
                        isActive: () => {
                            return qbdActive;
                        },
                    },
                ],
            });
        }

        if (supplierModel.Supplier_Integration.has_qbo) {
            actionsList.push({
                isDropdownGroup: true,
                name: "quickbooks_online",
                groupActions: [
                    {
                        name: "pull_vendors",
                        fn: pullQboVendors,
                        disabledFn: () => {
                            return qboActive;
                        },
                        isActive: () => {
                            return qboActive;
                        },
                    },
                    {
                        name: "push_vendors",
                        fn: showPushQboVendorsModal,
                        disabledFn: () => {
                            return qboActive;
                        },
                        isActive: () => {
                            return qboActive;
                        },
                    },
                    {
                        name: "connect_vendors",
                        fn: connectQboVendors,
                        disabledFn: () => {
                            return qboActive;
                        },
                        isActive: () => {
                            return qboActive;
                        },
                    },
                    {
                        name: "download_vendors",
                        fn: downloadQboVendors,
                        btnClass: () => {
                            return uiContext.showHiddenFeatures ? "btn-warning" : "d-none";
                        },
                        disabledFn: () => {
                            return qboActive;
                        },
                        isActive: () => {
                            return qboActive;
                        },
                    },
                ],
            });
        }

        return actionsList;
    }, [
        supplierModel.Supplier_Integration.has_qbd,
        supplierModel.Supplier_Integration.has_qbo,
        qboActive,
        qbdActive,
        uiContext.showHiddenFeatures,
        downloadVendorSourcesCSV,
        downloadVendorSourcesLocationsCSV,
        downloadVendorSourcesCatalogItemsCSV,
        showUploadModal,
        syncQbdVendors,
        pullQboVendors,
        downloadQboVendors,
    ]);

    // Columns array - memoized with dependencies
    const columns: IOwTableCols[] = useMemo(
        () => [
            {
                title: "",
                valueProp: "",
                isActionsCol: true,
                renderFn: renderActionsFn,
                renderFnEmitsRow: true,
            },
            {
                title: "name",
                valueProp: "",
                renderFn: renderNameFn,
                renderFnEmitsRow: true,
            },
            {
                title: "email",
                valueProp: "email",
            },
            {
                title: "bill_to",
                valueProp: "",
                renderFn: renderBillToFn,
                renderFnEmitsRow: true,
            },
            {
                title: "ship_to",
                valueProp: "",
                renderFn: renderShipToFn,
                renderFnEmitsRow: true,
            },
        ],
        [renderActionsFn, renderNameFn, renderBillToFn, renderShipToFn]
    );

    // Effect: Register socket listeners on mount, cleanup on unmount
    useEffect(() => {
        socket.on(EndpointsEnum.PurchaseOrderOnGetVendorSources, onGetVendorSources);
        socket.on(EndpointsEnum.PurchaseOrderOnGetVendorSourcesCSV, onGetVendorSourcesCSV);
        socket.on(EndpointsEnum.PurchaseOrderOnGetVendorSourcesLocationsCSV, onGetVendorSourcesLocationsCSV);
        socket.on(EndpointsEnum.PurchaseOrderOnGetVendorSourcesCatalogItemsCSV, onGetVendorSourcesCatalogItemsCSV);
        socket.on(EndpointsEnum.CatalogsOnGetCatalogs, onGetCatalogs);
        socket.on(EndpointsEnum.PurchaseOrderOnProcessVendorSourceCatalogItem, onProcessVendorSourceCatalogItem);
        socket.on(EndpointsEnum.QBOOnPullVendors, onPullQboVendors);
        socket.on(EndpointsEnum.QBOOnPullAndDownloadVendors, onDownloadQboVendors);
        socket.on(EndpointsEnum.PurchaseOrderOnProcessVendorSource, onProcessVendorSource);
        socket.on(EndpointsEnum.QBDOnCreateVendorSourcesFromVendors, onSyncQbdVendors);
        socket.on(EndpointsEnum.Notify, notify);

        return () => {
            socket.removeListener(EndpointsEnum.PurchaseOrderOnGetVendorSources, onGetVendorSources);
            socket.removeListener(EndpointsEnum.PurchaseOrderOnGetVendorSourcesCSV, onGetVendorSourcesCSV);
            socket.removeListener(EndpointsEnum.PurchaseOrderOnGetVendorSourcesLocationsCSV, onGetVendorSourcesLocationsCSV);
            socket.removeListener(EndpointsEnum.PurchaseOrderOnGetVendorSourcesCatalogItemsCSV, onGetVendorSourcesCatalogItemsCSV);
            socket.removeListener(EndpointsEnum.CatalogsOnGetCatalogs, onGetCatalogs);
            socket.removeListener(EndpointsEnum.PurchaseOrderOnProcessVendorSourceCatalogItem, onProcessVendorSourceCatalogItem);
            socket.removeListener(EndpointsEnum.QBOOnPullVendors, onPullQboVendors);
            socket.removeListener(EndpointsEnum.QBOOnPullAndDownloadVendors, onDownloadQboVendors);
            socket.removeListener(EndpointsEnum.PurchaseOrderOnProcessVendorSource, onProcessVendorSource);
            socket.removeListener(EndpointsEnum.QBDOnCreateVendorSourcesFromVendors, onSyncQbdVendors);
            socket.removeListener(EndpointsEnum.Notify, notify);
        };
    }, [
        socket,
        onGetVendorSources,
        onGetVendorSourcesCSV,
        onGetVendorSourcesLocationsCSV,
        onGetVendorSourcesCatalogItemsCSV,
        onGetCatalogs,
        onProcessVendorSourceCatalogItem,
        onPullQboVendors,
        onDownloadQboVendors,
        onProcessVendorSource,
        onSyncQbdVendors,
        notify,
    ]);

    // Effect: Initial data fetch on mount
    useEffect(() => {
        getVendorSources();
        getCatalogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Effect: Watch for URL parameter changes
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const urlNameParam = params.get("name");

        if (urlNameParam && urlNameParam !== name) {
            setName(urlNameParam);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // Effect: Fetch data when pagination changes
    useEffect(() => {
        if (!isInitialData) {
            getVendorSources();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offset, limit]);

    // Render
    let cardClass = "text-white bg-secondary dark-card";

    if (UIService.isMainViewLight()) {
        cardClass = "";
    }

    return (
        <div className="VendorSources Fn position-relative">
            <div>
                <VendorSourcesHeader notifyFn={uiContext.notifyFn} />
            </div>

            <div className="row mt-2">
                <div className={"col-12 d-flex align-items-center"}>
                    <OwTitle title={"vendor_sources"} leftIconName={"fad fa-handshake"} />
                    <div>
                        <OwButton
                            className={"ms-2 btn-warning"}
                            title={"create"}
                            leftIconClass={"fas fa-plus"}
                            fn={() => {
                                openVendorSourceSidebar();
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className={"mb-2"}>
                <SearchSection>
                    <div className={"d-flex"}>
                        <div className={"w-15r"}>
                            <OwLabel title={"name"} noBottomMargin={true} />
                            <OwInputWrapper name={"name"} value={name} fn={handleChange} onKeyDownFn={enterSearch} />
                        </div>

                        <div className={"ms-2"}>
                            <OwLabel title={""} noBottomMargin={true} />
                            <div>
                                <OwButton title={"search"} fn={search} />
                                <OwButton title={"reset"} fn={reset} className={"btn-danger ms-1"} />
                            </div>
                        </div>
                    </div>
                </SearchSection>
            </div>

            <div>
                <OwActionBar toggleSmallTable={() => {}} colToggledFn={() => {}} columns={{}} actions={actions} />
            </div>

            <div className="row mt-3 position-relative">
                <div className="col-12">
                    <ConfirmDeleteOverlayFn
                        handleConfirm={commitDelete}
                        handleCancel={cancelDelete}
                        message={"confirm_delete"}
                        targetElem={deleteRow}
                        leftAdjustment={-12}
                        nestedParentElem={"vendor-sources-table"}
                        skipMainView={true}
                    />

                    <OwTable
                        id={"vendor-sources-table"}
                        columns={columns}
                        dataSet={vendorSources}
                        userModel={userModel}
                        supplierModel={supplierModel}
                        dataFetch={isDataFetching}
                        initialData={isInitialData}
                        tableConfig={{ tableStriped: true }}
                    />
                </div>
            </div>

            <div className={"row"}>
                <div className={"col-12"}>
                    <PaginationBar
                        offset={offset}
                        limit={limit}
                        totalRecords={totalVendorSources}
                        onPaginationChanged={paginationChanged}
                    />
                </div>
            </div>

            <div>
                <Modal
                    isOpen={modalIsOpen}
                    contentLabel="Orderwerks"
                    shouldCloseOnOverlayClick={false}
                    className={"modalContentClass"}
                    overlayClassName={"modalOverlayClass"}>
                    {modalComponent}
                </Modal>
            </div>
        </div>
    );
};
